/*
 * Big B slideshow — autoplay sync + hover handling
 * Used by the "Labeled chips" and "Pill navigator" pagination styles.
 *
 * 1. Progress fill sync
 *    The theme's SlideshowComponent (global.js) calls play() when auto-rotate
 *    starts or resumes and pause() when it stops. Both are wrapped so:
 *      pause() adds .bb-nav-paused to <slideshow-component> → CSS freezes
 *              the fill on the active chip / dot
 *      play()  removes it and restarts the fill from 0, matching the fresh
 *              setInterval the theme starts on every play()
 *
 * 2. Hover handling for chips (no pause button)
 *    Dawn's own mouseover/mouseleave handlers assume a pause button exists.
 *    Without one, mouseover throws and mouseleave restarts the timer — so
 *    every time the cursor crosses the slideshow edge the countdown resets
 *    and the slide never advances. Those handlers were bound at construction
 *    and can't be patched, so mouse events are intercepted in the capture
 *    phase before they reach them. Hover pause is implemented here instead:
 *    pause on mouseover, resume after the section's "Pause on hover for"
 *    seconds unless something was clicked, resume immediately on mouseleave.
 *    Keyboard focus always pauses (a11y).
 *
 * Loaded from slideshow.liquid:
 *   <script src="{{ 'slideshow-navigator.js' | asset_url }}" defer="defer"></script>
 */
(function () {
  var PAUSED = 'bb-nav-paused';
  var ACTIVE = 'slider-counter__link--active';
  var ACTIVE_SELECTOR = '.bigb-chip.' + ACTIVE + ', .bb-nav .' + ACTIVE;

  function restartFill(component) {
    var active = component.querySelector(ACTIVE_SELECTOR);
    if (!active) return;
    active.classList.remove(ACTIVE);
    void active.offsetWidth; // force reflow so the animation actually restarts
    active.classList.add(ACTIVE);
  }

  function patchPrototype() {
    var Ctor = customElements.get('slideshow-component');
    if (!Ctor) return false;

    var proto = Ctor.prototype;
    if (proto.__bbNavPatched) return true;

    var originalPlay = proto.play;
    var originalPause = proto.pause;

    proto.play = function () {
      originalPlay.apply(this, arguments);
      this.classList.remove(PAUSED);
      restartFill(this);
    };

    proto.pause = function () {
      originalPause.apply(this, arguments);
      this.classList.add(PAUSED);
    };

    proto.__bbNavPatched = true;
    return true;
  }

  function setupHover(component) {
    if (component.__bbHoverSetup) return;
    if (component.querySelector('.slideshow__autoplay')) return; // theme handles it
    if (!component.classList.contains('bigb-chips')) return;
    component.__bbHoverSetup = true;

    var slider = component.querySelector('[id^="Slider-"]');
    var autoplayOn = slider && slider.getAttribute('data-autoplay') === 'true';
    if (!autoplayOn) return;

    // The theme's editor script only resumes auto-rotate after a block is
    // deselected when this flag is true — it's normally set alongside the
    // pause button, so without one the slideshow stays paused in the
    // customizer forever. Same flag, set by hand.
    component.autoplayButtonIsSetToPlay = true;

    // If the theme never started the timer (it skips play() for
    // reduced-motion users, and older versions bail without a button),
    // don't start it either when motion is reduced — but do otherwise.
    if (!component.autoplay && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      if (!component.autoplaySpeed) component.autoplaySpeed = (parseFloat(slider.dataset.speed) || 5) * 1000;
      component.play();
    }

    var hoverSeconds = parseFloat(component.dataset.bigbHoverPause);
    if (isNaN(hoverSeconds)) hoverSeconds = 3;
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var hovering = false;
    var clicked = false;
    var resumeTimer = null;

    function clearResume() {
      if (resumeTimer) clearTimeout(resumeTimer);
      resumeTimer = null;
    }

    // Capture phase runs before the theme's bubble-phase listeners on this
    // element, and stopPropagation keeps them from ever firing.
    component.addEventListener('mouseover', function (event) {
      event.stopPropagation();
      if (hovering || hoverSeconds <= 0 || reducedMotion) return;
      hovering = true;
      clicked = false;
      component.pause();
      // Resume after a short grace period unless the visitor clicked something
      clearResume();
      resumeTimer = setTimeout(function () {
        resumeTimer = null;
        if (hovering && !clicked && !component.matches(':focus-within')) component.play();
      }, hoverSeconds * 1000);
    }, true);

    component.addEventListener('mouseleave', function (event) {
      event.stopPropagation();
      clearResume();
      if (!hovering) return;
      hovering = false;
      clicked = false;
      if (!component.matches(':focus-within')) component.play();
    }, true);

    // A click means they're interacting: stay paused until the mouse leaves
    component.addEventListener('click', function () {
      if (!hovering) return;
      clicked = true;
      clearResume();
      component.pause();
    }, true);

    // Keyboard: pause while focus is inside. The theme's own focusout
    // handler resumes it on the way out.
    component.addEventListener('focusin', function () {
      component.pause();
    });
  }

  function setupAll() {
    document.querySelectorAll('slideshow-component').forEach(function (component) {
      // Mirror the state of instances that started before this script ran.
      // The theme sets aria-live="off" while playing and "polite" while paused.
      var slider = component.querySelector('[id^="Slider-"]');
      if (slider && slider.getAttribute('aria-live') === 'polite') {
        component.classList.add(PAUSED);
      }
      setupHover(component);
    });
  }

  function init() {
    if (!patchPrototype()) return false;
    setupAll();
    return true;
  }

  if (!init()) {
    customElements.whenDefined('slideshow-component').then(init);
  }

  // Theme editor re-renders the section on setting changes
  document.addEventListener('shopify:section:load', function () {
    setupAll();
  });
})();
